import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  IconButton,
  Tabs,
  Tab,
  Alert,
  Chip,
} from '@mui/material';
import {
  Close,
  GetApp,
  PhoneAndroid,
  Share,
  AddBox,
  MoreVert,
  CheckCircle,
  Apple,
} from '@mui/icons-material';

interface PWAInstallPromptProps {
  open: boolean;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`install-tabpanel-${index}`}
      aria-labelledby={`install-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({ open, onClose }) => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const chromeSteps = [
    {
      label: 'Ouvrez le menu',
      description: 'Appuyez sur les trois points verticaux (⋮) en haut à droite de Chrome',
      icon: <MoreVert sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      label: 'Sélectionnez "Installer l\'application"',
      description: 'Dans le menu, cherchez et appuyez sur "Installer l\'application" ou "Ajouter à l\'écran d\'accueil"',
      icon: <AddBox sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      label: 'Confirmez l\'installation',
      description: 'Appuyez sur "Installer" dans la popup qui apparaît',
      icon: <GetApp sx={{ fontSize: 40, color: 'success.main' }} />,
    },
    {
      label: 'C\'est terminé !',
      description: 'L\'application apparaîtra sur votre écran d\'accueil. Vous pouvez maintenant l\'utiliser comme une application native.',
      icon: <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />,
    },
  ];

  const safariSteps = [
    {
      label: 'Ouvrez le menu de partage',
      description: 'Appuyez sur le bouton de partage (carré avec une flèche vers le haut) en bas de Safari',
      icon: <Share sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      label: 'Faites défiler le menu',
      description: 'Dans le menu qui s\'ouvre, faites défiler vers le bas',
      icon: <MoreVert sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      label: 'Sélectionnez "Sur l\'écran d\'accueil"',
      description: 'Appuyez sur "Sur l\'écran d\'accueil" (Add to Home Screen)',
      icon: <AddBox sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      label: 'Nommez l\'application',
      description: 'Donnez un nom à l\'application (ou gardez le nom par défaut) puis appuyez sur "Ajouter"',
      icon: <GetApp sx={{ fontSize: 40, color: 'success.main' }} />,
    },
    {
      label: 'C\'est terminé !',
      description: 'L\'icône apparaît sur votre écran d\'accueil. Ouvrez-la pour utiliser l\'application.',
      icon: <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneAndroid color="primary" />
            <Typography variant="h6" component="span">
              Installer l'application
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Installez l'application sur votre téléphone pour recevoir des notifications
            instantanées et accéder à vos dossiers ANEF même hors ligne.
          </Typography>
        </Alert>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneAndroid />
                  <span>Android / Chrome</span>
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Apple />
                  <span>iPhone / Safari</span>
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Android / Chrome */}
        <TabPanel value={tabValue} index={0}>
          <Paper elevation={0} sx={{ bgcolor: 'grey.50', p: 2, mb: 3 }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              📱 Pour Android avec Chrome
            </Typography>
            <Typography variant="body2" color="text.secondary">
              L'installation est simple et rapide. Suivez ces étapes :
            </Typography>
          </Paper>

          <Stepper orientation="vertical">
            {chromeSteps.map((step, index) => (
              <Step key={index} active={true} completed={false}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'primary.light',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {index + 1}
                    </Box>
                  )}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {step.icon}
                    <Typography variant="body1">{step.description}</Typography>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              💡 Astuce : Une fois installée, l'application fonctionnera même sans connexion
              internet pour consulter vos dossiers sauvegardés !
            </Typography>
          </Alert>
        </TabPanel>

        {/* iPhone / Safari */}
        <TabPanel value={tabValue} index={1}>
          <Paper elevation={0} sx={{ bgcolor: 'grey.50', p: 2, mb: 3 }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              🍎 Pour iPhone avec Safari
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Installation en quelques étapes simples :
            </Typography>
          </Paper>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              ⚠️ Important : L'installation ne fonctionne que sur Safari (navigateur par défaut
              d'Apple). Si vous utilisez Chrome sur iPhone, ouvrez d'abord ce site dans Safari.
            </Typography>
          </Alert>

          <Stepper orientation="vertical">
            {safariSteps.map((step, index) => (
              <Step key={index} active={true} completed={false}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'primary.light',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {index + 1}
                    </Box>
                  )}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {step.icon}
                    <Typography variant="body1">{step.description}</Typography>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              💡 Astuce : Une fois installée, l'icône apparaîtra sur votre écran d'accueil
              comme n'importe quelle autre application iOS !
            </Typography>
          </Alert>
        </TabPanel>

        {/* Section commune */}
        <Paper elevation={2} sx={{ mt: 4, p: 3, bgcolor: 'primary.light', color: 'white' }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            ✨ Avantages de l'installation
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body2">
                📬 Notifications push instantanées pour les compléments ANEF
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                📱 Accès rapide depuis votre écran d'accueil
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                ⚡ Chargement ultra-rapide
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                🔄 Consultation hors ligne de vos dossiers
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                💾 Utilise moins d'espace qu'une application classique
              </Typography>
            </li>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" size="large">
          Fermer
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          startIcon={<CheckCircle />}
        >
          J'ai compris
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PWAInstallPrompt;
